import React, { useMemo, useContext } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { DealDetail, UserPreference } from 'api'
import { Translation, Locale } from 'locale'
import { WindowActions } from 'epics/windowEpic'
import {
  HeroPanel,
  BannerStatusTile,
  BannerTilesGroup,
  DetailTilesContainer,
  EnvironmentTitle,
  BannerCurrencyTile,
  getDealCashAmounts
} from 'view/shared/heroPanel'
import { EnvironmentContext } from 'view/shared/environment'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'

interface DealsHeroPanelBaseProps {
  dealList: DealDetail[]
  userPreference: UserPreference
  onTearout: (url: string) => void
  tearoutRoute: string
  heroTitle: Translation
  bannerTitle: Translation
}

const mapStateToProps = createStructuredSelector({
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onTearout: WindowActions.tearoutGrid
}

type CashAmountsTilesProps = Pick<DealsHeroPanelBaseProps, 'dealList' | 'userPreference'>

const CashAmountsTiles: React.FunctionComponent<CashAmountsTilesProps> = ({ dealList, userPreference }) => {
  const cashAmounts = useMemo(() => getDealCashAmounts(dealList), [dealList])

  if (cashAmounts.length > 0) {
    return (
      <DetailTilesContainer large={true} flexDirection="row">
        {cashAmounts.map(cashAmount => (
          <BannerTilesGroup key={cashAmount[0].currency} title={cashAmount[0].currency}>
            <BannerCurrencyTile
              cashAmount={cashAmount[0]}
              notional={cashAmount[1]}
              cashLabel={Locale.translate(Translation.TC_TADJ_CASH)}
              gross={true}
              userPreference={userPreference}
            />
          </BannerTilesGroup>
        ))}
      </DetailTilesContainer>
    )
  }
  return null
}

const DealsHeroPanelBaseComponent: React.FunctionComponent<DealsHeroPanelBaseProps> = props => {
  const { dealList, userPreference, onTearout, tearoutRoute, heroTitle, bannerTitle } = props
  const environment = useContext(EnvironmentContext)

  return (
    <HeroPanel onTearout={() => onTearout(tearoutRoute)}>
      <DetailTilesContainer>
        <EnvironmentTitle data-test-id={'DealsHeroPanel-EnvironmentTitle'}>{Locale.translate(heroTitle)}</EnvironmentTitle>
        <BannerTilesGroup title={Locale.translate(Translation.TC_HERO_STATUS)}>
          <BannerStatusTile title={Locale.translate(bannerTitle)} label={dealList.length} />
        </BannerTilesGroup>
      </DetailTilesContainer>
      {environment.isAdmin && <CashAmountsTiles dealList={dealList} userPreference={userPreference} />}
    </HeroPanel>
  )
}

export const DealsHeroPanelBase = connect(mapStateToProps, mapDispatchToProps)(DealsHeroPanelBaseComponent)
